<script>
	import { DateTime } from "luxon";
	import { v4 as uuidv4 } from 'uuid';
	import { google, ics } from "calendar-link";
	import { toOutlook365Link, toOutlookLink } from "./calendarLinks";

	import Banner from './Banner.svelte';
	import Calendar from './Calendar.svelte';
	import Duration from './Duration.svelte';
	import ScheduleModal from './ScheduleModal.svelte'
	import Help from './Help.svelte'

	let eventDate = DateTime.local();
	let startTime = DateTime.local().startOf('hour').plus({hours: 1});
	let endTime = startTime.plus({minutes: 30});
	let eventTitle = '';
	let guid = uuidv4();

	let showModal = false;

	$: view = eventDate.toLocaleString(DateTime.DATE_HUGE) + "&nbsp;&nbsp;&nbsp;&nbsp;" + startTime.toLocaleString(DateTime.TIME_SIMPLE) + " – " + endTime.toLocaleString(DateTime.TIME_SIMPLE)
	$: eventLink = "https://meet.jit.si/" + guid;
	$: eventStart = DateTime.local(eventDate.year, eventDate.month, eventDate.day, startTime.hour, startTime.minute);
	$: eventEnd = DateTime.local(eventDate.year, eventDate.month, eventDate.day, endTime.hour, endTime.minute);
	$: eventDesc = 'Join Jitsi Meet video call: ' + eventLink + '\n\n\n<em>Scheduled with <a href="https://helpmeetcal.com">Help Meet</a></em>';
	$: calendarEvent = {
		start: eventStart,
		end: eventEnd,
		title: eventTitle,
		description: eventDesc,
		location: eventLink,
		url: eventLink
	}

	function enableModal() {
		guid = uuidv4();
		showModal = true;
		window.scroll(0, 0);
	}

	function disableModal() {
		showModal = false;
	}

</script>

<svelte:head>
	<link href="https://fonts.googleapis.com/css2?family=Comfortaa:wght@700&display=swap" rel="stylesheet">
	<link href="https://fonts.googleapis.com/css2?family=Montserrat&display=swap" rel="stylesheet">
</svelte:head>

<main>
	<div id="form" class="form">
		<h1 id="main-title">Help Meet</h1>
		<Banner />
		<Calendar bind:selectedDate={eventDate}/>
		<Duration bind:fromTime={startTime} bind:toTime={endTime}/>
		<div class="title-entry">
			<label for="event-title">Event Title</label>
			<input type="text" bind:value={eventTitle} placeholder="add title...">
		</div>
		<div class="schedule-desc">{@html view}</div>
		<button class="schedule-btn" on:click="{enableModal}">
			Schedule It!
		</button>
	</div>

	<Help />

	<div class="footer">
		<hr>
		<div class="footer-grid">
			&copy; {DateTime.local().year} Help Meet | <a href="mailto:contact@helpmeetcal.com">Contact</a> | <a href="https://twitter.com/HelpMeetCal">@HelpMeetCal</a>
		</div>
	</div>

{#if showModal}
<ScheduleModal on:close="{disableModal}">
	<div slot="modal-header">
		<h2>Add {eventTitle ? "\"" + eventTitle + "\"": 'event'} to your calendar</h2>
		{@html view}
	</div>
	<div class="modal-main">
		<div class="link-list">
			<a class="save-link" href="{google(calendarEvent)}" target="_blank" rel="noopener noreferrer">
				<img class="save-link-logo" type="image/svg+xml" src="images/Google__G__Logo.svg" alt="Google G Logo" />
				Google
			</a>
			<a class="save-link" href="{ics(calendarEvent)}" download="{eventTitle ? eventTitle.replace(/\s/g,'-') : 'event'}.ics">
				<img class="save-link-logo" type="image/svg+xml" src="images/Apple_logo_grey.svg" alt="Apple Logo" />
				Apple
			</a>
			<a class="save-link" href="{toOutlook365Link(calendarEvent)}" target="_blank" rel="noopener noreferrer">
				<img class="save-link-logo" type="image/svg+xml" src="images/Logo_Microsoft_Office_365.svg" alt="Office 365 Logo" />
				Office 365
			</a>
			<a class="save-link" href="{toOutlookLink(calendarEvent)}" target="_blank" rel="noopener noreferrer">
				<img class="save-link-logo" type="image/svg+xml" src="images/Outlook.com_icon.svg" alt="Outlook Logo" />
				Outlook.com
			</a>
			<a class="save-link" href="{ics(calendarEvent)}" download="{eventTitle ? eventTitle.replace(/\s/g,'-') : 'event'}.ics">
				<img class="save-link-logo" type="image/svg+xml" src="images/Feather-core-calendar.svg" alt="Apple Logo" />
				iCal
			</a>
		</div>
		<div>
			<p class="modal-help">
				Clicking on the above links will create a new event in your calendar of choice. From there you
				can add attendees, additional event information, and even make it recurring. When the time for the  
				event comes everyone just needs to click on the unique 
				<a href="https://meet.jit.si/{guid}" target="_blank" rel="noopener noreferrer">https://meet.jit.si/</a> 
				link in the event to join the video conference.
			</p>
		</div>
	</div>
</ScheduleModal>
{/if}

</main>

<style>
	:global(:root) {
		/* Application colors */
		--red-main: #ef402bff;
		--red-ligher: #F9BAB3ff;
		--red-darker: #711409ff;

		--grn-main: #a9e5bbff;
		--grn-lighter: #EFFAF3ff;
		--grn-darker: #194d2dff;

		--ylw-main: #fcf6b1ff;
		--ylw-lighter: #FEFDEBff;
		--ylw-darker: #272502ff;

		--blugry-main: #373F51ff;
		--blugry-lighter: #dbdee6ff;
		--blugry-darker: #08090Cff;
		--blugry-lightest: #F3F4F7;
	}

	:global(body) {
		font-family: Montserrat, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
		color: var(--blugry-darker);
		background-color: var(--blugry-lightest);
	}

	:global(label) {
		font-size: 1.1em;
	}

	:global(h1) {
		color: var(--red-main);
		font-family: 'Comfortaa', sans-serif;
		font-size: 4em;
	}

	main {
		text-align: center;
		padding: 1em;
		max-width: 320px;
		margin: 0 auto;
	}

	.title-entry {
		width: max-content;
		display: inline-block;
		margin-bottom: 1.5em;
	}

	.title-entry input {
		border: 1px solid var(--blugry-main);
		border-radius: 8px;
		width: 50ch;
	}

	.schedule-desc {
		font-size: 1.25em;
	}

	.schedule-btn {
		font-weight: bold;
		font-size: 1.25em;
		border: 1px solid var(--red-darker);
		border-radius: 8px;
		background-color: var(--red-main);
		color: white;
		margin-top: 2em;
		padding-top: 1.25em;
		padding-bottom: 1.25em;
		padding-left: 3em;
		padding-right: 3em;
	}

	.schedule-btn:active:not(:disabled) {
		box-shadow: -1px -1px 1px var(--blugry-darker);
		background-color: #C9230D;
	}

	.link-list {
		display: grid;
		grid-template-columns: repeat(1, 1fr);
		grid-column-gap: 5px;
		grid-row-gap: 5px;
	}

	.save-link {
		grid-column: 1;
		text-decoration: none;
		margin: 5px;
		padding: 0.5em;
		border: 1px solid var(--blugry-lighter);
		color: var(--blugry-darker);
	}

	.save-link-logo {
		width: 30px;
		vertical-align: middle;
	}

	.modal-help {
		font-size: 0.75em;
		line-height: 1.1em;
	}

	@media (min-width: 640px) {
		main {
			max-width: none;
		}
	}

	@media (max-width: 640px) {
		.title-entry input {
			width: 30ch;
		}
	}

	@media (max-width: 320px) {
		.title-entry input {
			width: 25ch;
		}

		.schedule-btn {
			padding-top: 1.0em;
			padding-bottom: 1.0em;
			padding-left: 2em;
			padding-right: 2em;
		}
	}
</style>
